/**
 * 学习乐园 · 排排序（game-paixu.js）
 * ============================================================
 * 一页多题材的「把乱序排回正确顺序」通用引擎，与知识连连看完全同构：
 * 题材数据不在本文件——由 paixu/pack-*.js 注册进全局 STPX_PACKS
 * （footer 按 config/study.php 的 packs 字段按需加载，defer 文档序保证
 * 先于本文件执行）。引擎只认 items 的顺序号，与题材解耦：新增题材零改引擎。
 *
 * 交互（刻意不做拖拽——红线条目内说明过）：点第一张选中，再点另一张交换。
 * 两击交换在鼠标与触屏上的手感完全一致，不需要处理 HTML5 DnD 在移动端的
 * 兼容与 ghost 影子；换位心智与连连看的点选一致，孩子不用重新学操作。
 *
 * 反馈口径（栏目底线——即时且可解释）：交换后不评判（让玩家自己想），
 * 「查一查」随时可点：从左往右找出第一个放错的位置，说清「第几位应该是
 * 什么、现在放的是什么、为什么」（note 由题材包逐项提供）。查的次数如实
 * 计入结算。
 *
 * 架构（与棋类/连连看同口径）：零 vendor、零请求；成绩与偏好只存本机
 * localStorage；一局两三分钟，不做中断续局，刷新即重开。
 *
 * 冒烟钩子：window.STPaixu 暴露纯引擎函数（shuffled / isSolved /
 * firstWrong / wrongExplain），供冒烟与单测跑确定性用例。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-paixu';
  var RECORD_KEY = 'study-paixu-record';
  var BAD_MS = 420;

  /* ============ 引擎：洗牌与判定（纯函数） ============ */

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  // 从题材 items（正确顺序）随机抽 count 张乱序开局——m 选 n：池子比局大时
  // 每局换内容，不只是换位置。返回卡片数组 [{ order, face, note, html }]，
  // order = 抽出子集内的正确位次（0 起、连续重排，子集保持原相对顺序）。
  // 乱序结果不得恰好等于正确顺序（等于就没得排）——count>1 时重洗，最多 60 次。
  function shuffled(items, count) {
    var n = Math.max(1, Math.min(count || items.length, items.length));
    var picked = items.slice();
    if (n < picked.length) {
      shuffle(picked);
      picked = picked.slice(0, n);
    }
    picked.sort(function (a, b) { return items.indexOf(a) - items.indexOf(b); });   // 子集回到原相对顺序
    var seq = null;
    for (var attempt = 0; attempt < 60; attempt++) {
      seq = shuffle(picked.slice());
      var same = true;
      for (var i = 0; i < seq.length; i++) {
        if (seq[i] !== picked[i]) { same = false; break; }
      }
      if (!same || seq.length < 2) break;
    }
    var cards = [];
    for (var k = 0; k < seq.length; k++) {
      var it = seq[k];
      cards.push({
        order: picked.indexOf(it),         // 子集内位次；items 内 face 不得重复（题材包红线）
        face: it.face,
        note: it.note || '',
        html: it.html || ''
      });
    }
    return cards;
  }

  // 是否已排对：每张卡的 order 都等于当前位置
  function isSolved(seq) {
    for (var i = 0; i < seq.length; i++) {
      if (seq[i].order !== i) return false;
    }
    return true;
  }

  // 第一个放错的位置（从左往右扫）；全对返回 -1
  function firstWrong(seq) {
    for (var i = 0; i < seq.length; i++) {
      if (seq[i].order !== i) return i;
    }
    return -1;
  }

  // 错位反馈（栏目底线：说清为什么不对）——说位置、该是谁、现在是谁、为什么
  function wrongExplain(pos, cur, want) {
    var tail = want.note ? '——' + want.note : '';
    return '第 ' + (pos + 1) + ' 位应该是「' + want.face + '」，现在放的是「' + cur.face + '」' + tail
      + '。把两张点一下换过来再查。';
  }

  /* ============ UI：挂载与状态 ============ */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  var root = null;
  var ui = {};
  var S = {
    packId: '', soundOn: true, pack: null,
    cols: 0,
    cards: [],            // [{order, face, note, html}]
    sel: null,            // 第一张选中的下标
    swaps: 0, checks: 0,
    elapsed: 0, timer: null,
    ended: false,
    resetArm: false,
    resetTimer: null
  };

  /* ---- 题材选择器（与连连看同构） ---- */

  function renderPackButtons() {
    var box = ui.packs;
    box.textContent = '';
    var ids = Object.keys(global.STPX_PACKS || {});
    if (!ids.length) {
      box.appendChild(el('p', 'st-llk-none', '题材包没有加载出来，刷新一次试试。'));
      return;
    }
    for (var i = 0; i < ids.length; i++) {
      (function (id) {
        var p = global.STPX_PACKS[id];
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-pack', id);
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', p.name));
        b.appendChild(el('small', 'st-llk-pack-hint', p.hint));
        b.addEventListener('click', function () {
          if (S.packId === id) return;
          newGame(id);   // 换题材 = 直接开新局（一局本来就两三分钟）
        });
        box.appendChild(b);
      })(ids[i]);
    }
  }

  function syncPackButtons() {
    var btns = ui.packs.querySelectorAll('[data-pack]');
    for (var i = 0; i < btns.length; i++) {
      var on = btns[i].getAttribute('data-pack') === S.packId;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- 卡片网格 ---- */

  function buildBoard() {
    var board = ui.board;
    board.textContent = '';
    board.style.setProperty('--st-px-cols', String(S.cols));
    ui.cells = [];
    for (var i = 0; i < S.cards.length; i++) {
      (function (idx) {
        var b = el('button', 'st-px-card');
        b.type = 'button';
        b.appendChild(el('span', 'st-px-slot'));
        b.appendChild(el('span', 'st-px-face'));
        b.addEventListener('click', function () { onCard(idx); });
        board.appendChild(b);
        ui.cells.push(b);
      })(i);
    }
    renderCards();
  }

  function renderCards() {
    for (var i = 0; i < S.cards.length; i++) {
      var btn = ui.cells[i];
      var card = S.cards[i];
      var slot = btn.firstChild;
      var face = btn.lastChild;
      // 读屏可闻：槽位 + 牌面 + 瞬时态（只报序号等于让视障用户盲猜，必须带牌面）
      var label = '第 ' + (i + 1) + ' 位，' + card.face;
      if (S.sel === i) label += '，已选中';
      btn.setAttribute('aria-label', label);
      slot.textContent = String(i + 1);
      if (card.html) { face.innerHTML = card.html; face.classList.add('st-px-face--html'); }
      else { face.textContent = card.face; face.classList.remove('st-px-face--html'); }
      btn.classList.toggle('is-sel', S.sel === i);
    }
  }

  /* ---- 渲染：状态 / 反馈 / 成绩 / 按钮 ---- */

  function fmtTime(sec) {
    var m = Math.floor(sec / 60), s = sec % 60;
    return m + ' 分 ' + (s < 10 ? '0' : '') + s + ' 秒';
  }

  function fmtTimeShort(sec) {
    var m = Math.floor(sec / 60), s = sec % 60;
    return m ? m + ' 分 ' + (s < 10 ? '0' : '') + s + ' 秒' : s + ' 秒';
  }

  function renderStatus() {
    if (S.ended) return;
    ui.status.className = 'st-status';
    ui.status.textContent = '共 ' + S.cards.length + ' 张 · 换了 ' + S.swaps + ' 次'
      + ' · 用时 ' + fmtTime(S.elapsed);
  }

  function renderNote(text, tone) {
    ui.note.textContent = text || '';
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord()[S.packId];
    if (!rec || !rec.wins) {
      box.className = 'st-llk-record is-empty';
      box.appendChild(el('p', 'st-llk-record-empty', '这个题材还没通关过——排对一次，这里记下你的最好成绩。'));
      return;
    }
    box.className = 'st-llk-record';
    box.appendChild(recordStat('通关', rec.wins + ' 次'));
    box.appendChild(recordStat('最好用时', fmtTimeShort(rec.bestTime)));
    box.appendChild(recordStat('那局交换', rec.bestSwaps + ' 次'));
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  function renderButtons() {
    ui.checkBtn.disabled = S.ended;
    ui.newBtn.disabled = false;
    ui.resetBtn.disabled = false;
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置这个题材的成绩';
  }

  function renderAll() {
    renderCards();
    renderStatus();
    renderButtons();
  }

  /* ---- 音效（WebAudio 本地合成，与连连看同一套音色表） ---- */

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0], [659, 0.07]];
      else if (kind === 'win') seq = [[523, 0], [659, 0.1], [784, 0.2], [1046, 0.3]];
      else if (kind === 'bad') seq = [[196, 0], [185, 0.09]];
      else seq = [[330, 0]];   // sel
      for (var i = 0; i < seq.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0], t + seq[i][1]);
        g.gain.setValueAtTime(0.0001, t + seq[i][1]);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.18, t + seq[i][1] + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seq[i][1] + (SFX_MOBILE ? 0.2 : 0.14));
        o.connect(g);
        g.connect(actx.destination);
        o.start(t + seq[i][1]);
        o.stop(t + seq[i][1] + (SFX_MOBILE ? 0.26 : 0.16));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* ---- 计时 ---- */

  function startTimer() {
    stopTimer();
    S.elapsed = 0;
    S.timer = setInterval(function () {
      if (S.ended) return;
      S.elapsed++;
      renderStatus();
    }, 1000);
  }

  function stopTimer() {
    if (S.timer) { clearInterval(S.timer); S.timer = null; }
  }

  /* ---- 交互：点两下交换 ---- */

  function onCard(idx) {
    if (S.ended) return;
    if (S.sel === idx) {   // 再点自己 = 取消选中
      S.sel = null;
      renderAll();
      return;
    }
    if (S.sel === null) {
      S.sel = idx;
      sfx('sel');
      renderAll();
      return;
    }
    var a = S.sel, b = idx;
    S.sel = null;
    var t = S.cards[a]; S.cards[a] = S.cards[b]; S.cards[b] = t;
    S.swaps++;
    sfx('ok');
    if (isSolved(S.cards)) {
      renderAll();
      winGame();
      return;
    }
    // 换完不评判——想自己找对错；「查一查」随时可点（不抢思考的乐趣，反馈也不会迟到太久）
    renderAll();
  }

  /* ---- 查一查：指出第一个错位并讲原因 ---- */

  function checkOrder() {
    if (S.ended) return;
    var pos = firstWrong(S.cards);
    if (pos < 0) return;   // 通关在 onCard 里就结束，理论不可达（防御）
    S.checks++;
    var want = null;
    for (var i = 0; i < S.cards.length; i++) {
      if (S.cards[i].order === pos) { want = S.cards[i]; break; }
    }
    flashBad([pos]);
    sfx('bad');
    renderNote(wrongExplain(pos, S.cards[pos], want), 'bad');
  }

  function flashBad(idxs) {
    for (var i = 0; i < idxs.length; i++) {
      ui.cells[idxs[i]].classList.add('is-bad');
    }
    setTimeout(function () {
      for (var j = 0; j < idxs.length; j++) {
        var b = ui.cells[idxs[j]];
        if (b && b.isConnected) b.classList.remove('is-bad');
      }
    }, BAD_MS);
  }

  /* ---- 通关与成绩（只存本机） ---- */

  function winGame() {
    S.ended = true;
    stopTimer();
    sfx('win');
    var rec = loadRecord();
    var cur = rec[S.packId];
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) {
      cur = { plays: 0, wins: 0, bestTime: 0, bestSwaps: 0 };
    }
    cur.plays++;
    cur.wins++;
    var isBest = !cur.bestTime || S.elapsed < cur.bestTime;
    if (isBest) {
      cur.bestTime = S.elapsed;
      cur.bestSwaps = S.swaps;
    }
    rec[S.packId] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 隐私模式静默 */ }
    ui.status.className = 'st-status is-over';
    ui.status.textContent = '排对了！用时 ' + fmtTime(S.elapsed) + '，交换 ' + S.swaps
      + ' 次，查了 ' + S.checks + ' 次。'
      + (isBest ? '这是这个题材的最好成绩。' : '（最好成绩 ' + fmtTime(cur.bestTime) + '）')
      + '点「开始新一局」再来一把。';
    renderNote(S.pack && S.pack.winText ? S.pack.winText : '顺序就是规律——再排一遍，这次少查几次。', 'ok');
    renderRecord();
    renderButtons();
  }

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try {
      var rec = loadRecord();
      delete rec[S.packId];
      localStorage.setItem(RECORD_KEY, JSON.stringify(rec));
    } catch (e) { /* 隐私模式静默 */ }
    renderRecord();
    renderButtons();
    toast('已重置「' + (S.pack ? S.pack.name : '') + '」的本机成绩');
  }

  /* ---- 偏好（题材/音效）存本机 ---- */

  function save() {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, packId: S.packId, soundOn: S.soundOn }));
    } catch (e) { /* 存储不可用静默（无痕模式） */ }
  }

  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (!d || d.v !== 1) return;
      S.packId = typeof d.packId === 'string' ? d.packId : '';
      S.soundOn = d.soundOn !== false;
    } catch (e) { /* 忽略脏数据 */ }
  }

  /* ---- 开局 ---- */

  function newGame(packId) {
    var packs = global.STPX_PACKS || {};
    // 首访（无本机偏好）或偏好里的题材已下架：兜底取注册序第一个，页面不能空着
    if (!packs[packId]) packId = Object.keys(packs)[0] || '';
    var pack = packs[packId];
    if (!pack) return;
    S.pack = pack;
    S.packId = packId;
    S.cols = pack.cols || 6;
    S.cards = shuffled(pack.items, pack.draw || pack.items.length);
    S.sel = null;
    S.swaps = 0;
    S.checks = 0;
    S.ended = false;
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    syncPackButtons();
    buildBoard();
    startTimer();
    renderAll();
    renderRecord();
    renderNote('「' + pack.name + '」：点一张选中，再点另一张就交换；觉得排好了点「查一查」。', '');
    save();
  }

  /* ---- 音效选择器（口径同五子棋/连连看） ---- */

  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }

  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- toast（口径同五子棋/连连看） ---- */

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.packs = root.querySelector('[data-st-px-packs]');
    ui.board = root.querySelector('[data-st-px-board]');
    ui.status = root.querySelector('[data-st-px-status]');
    ui.note = root.querySelector('[data-st-px-note]');
    ui.record = root.querySelector('[data-st-px-record]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.checkBtn = root.querySelector('[data-st-check]');
    ui.resetBtn = root.querySelector('[data-st-reset]');

    restorePrefs();
    renderPackButtons();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(S.packId); });
    ui.checkBtn.addEventListener('click', checkOrder);
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame(S.packId);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STPaixu = {
    shuffle: shuffle,
    shuffled: shuffled,
    isSolved: isSolved,
    firstWrong: firstWrong,
    wrongExplain: wrongExplain
  };
})(typeof window !== 'undefined' ? window : globalThis);
